import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { getTokens, hexToRgba } from './tokens';
import type { Tokens } from './tokens';
import { useTheme, useAccent } from './ThemeContext';

// The shared stylesheet. Every key that the 21 per-module getStyles()
// functions used to define lives here exactly once.
//
// Migration recipe for a module:
//   1. delete its `const getStyles = (isDarkMode) => ({...})` block
//   2. replace `const styles = getStyles(isDarkMode)` with `const styles = useStyles(isDarkMode)`
//   3. keep any module-specific keys by spreading: { ...styles, myKey: {...} }

/**
 * Every key the shared stylesheet defines. Listing them as a union means a
 * typo like `styles.headerTitel` is a compile error instead of a silently
 * unstyled element — the main reason this file is typed at all.
 */
export type StyleKey =
  | 'actionBtn' | 'activeTabBtn' | 'addBtn' | 'ajaxSearchInput'
  | 'backBtn' | 'badge' | 'card' | 'container'
  | 'dangerBtn' | 'dangerTabBtn' | 'deleteBtn' | 'drawerBody'
  | 'drawerCloseBtn' | 'drawerFooter' | 'drawerHeader' | 'drawerLabel'
  | 'drawerOverlay' | 'drawerPanel' | 'drawerRow' | 'drawerTitle'
  | 'drawerValue' | 'dropdownFooter' | 'dropdownItem' | 'dropdownMenu'
  | 'emptyCell' | 'formBody' | 'formFooter' | 'formGrid'
  | 'formHeaderContainer' | 'formHeaderTitle' | 'formWrapper' | 'fullWidth'
  | 'headerRow' | 'headerTitle' | 'inactiveTabBtn' | 'input'
  | 'inputGroup' | 'label' | 'notificationBox' | 'notificationBtn'
  | 'notificationOverlay' | 'notificationText' | 'notificationTitle' | 'pageBtn'
  | 'pageInfo' | 'pagination' | 'searchInput' | 'sectionTitle'
  | 'submitBtn' | 'table' | 'tableContainer' | 'td'
  | 'th' | 'tr' | 'viewBtn' | 'widgetBox'
  | 'widgetHeader' | 'widgetTitle';

/** The stylesheet plus the raw tokens, for one-off inline overrides. */
export type AppStyles = Record<StyleKey, CSSProperties> & { t: Tokens };

export const buildStyles = (isDarkMode: boolean, accent?: string): AppStyles => {
  const t = getTokens(isDarkMode, accent);

  return {
    t, // tokens available for one-off inline overrides

    // --- Page shell ---
    // Surfaces carry the theme's text colour as well as its background, so a
    // line that sets no colour of its own is not left in the browser's black
    // on a dark panel.
    container: {
      backgroundColor: t.bgMain,
      color: t.textMain,
      borderRadius: t.radius.xl,
      border: `1px solid ${t.border}`,
      boxShadow: t.shadowCard,
      padding: t.spacing.xxl,
      width: '100%',
      boxSizing: 'border-box'
    },
    // Title row; the tab bar beneath it draws its own rule.
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: t.spacing.md,
      flexWrap: 'wrap',
      gap: t.spacing.sm
    },
    headerTitle: {
      color: t.textMain,
      margin: 0,
      fontSize: t.fontSize.xxl,
      fontWeight: t.fontWeight.black,
      letterSpacing: '-0.01em'
    },
    sectionTitle: {
      marginTop: t.spacing.lg,
      marginBottom: t.spacing.sm,
      fontSize: t.fontSize.md,
      color: t.textMain,
      fontWeight: t.fontWeight.bold,
      borderBottom: `1px solid ${t.border}`,
      paddingBottom: t.spacing.xs
    },

    // --- Tabs / buttons ---
    // Pill pair used by screens that draw their own tab rows; the shared
    // PageHeader / SubTabs components draw an underline bar and a segmented
    // control instead, so these two only need to read as one family.
    activeTabBtn: {
      backgroundColor: t.primary,
      border: `1px solid ${t.primary}`,
      color: t.textOnPrimary,
      padding: '7px 14px',
      marginLeft: 0,
      borderRadius: t.radius.pill,
      cursor: 'pointer',
      fontWeight: t.fontWeight.bold,
      fontSize: t.fontSize.base
    },
    inactiveTabBtn: {
      backgroundColor: t.bgMain,
      border: `1px solid ${t.borderStrong}`,
      color: t.textMuted,
      padding: '7px 14px',
      marginLeft: 0,
      borderRadius: t.radius.pill,
      cursor: 'pointer',
      fontWeight: t.fontWeight.medium,
      fontSize: t.fontSize.base
    },
    submitBtn: {
      backgroundColor: t.primary,
      color: t.textOnPrimary,
      padding: '9px 16px',
      border: `1px solid ${t.primary}`,
      borderRadius: t.radius.md,
      cursor: 'pointer',
      fontSize: t.fontSize.md,
      fontWeight: t.fontWeight.bold
    },
    viewBtn: {
      backgroundColor: t.bgMain,
      border: `1px solid ${t.borderStrong}`,
      color: t.textMain,
      padding: '8px 14px',
      borderRadius: t.radius.md,
      cursor: 'pointer',
      fontWeight: t.fontWeight.bold,
      fontSize: t.fontSize.base
    },
    actionBtn: {
      backgroundColor: 'transparent',
      border: '1px solid transparent',
      color: t.textAccent,
      padding: '8px 12px',
      borderRadius: t.radius.md,
      cursor: 'pointer',
      fontWeight: t.fontWeight.bold,
      fontSize: t.fontSize.base
    },
    dangerBtn: {
      backgroundColor: 'transparent',
      border: `1px solid ${hexToRgba(t.danger, 0.35)}`,
      color: t.danger,
      padding: '8px 12px',
      borderRadius: t.radius.md,
      cursor: 'pointer',
      fontWeight: t.fontWeight.bold,
      fontSize: t.fontSize.base
    },
    backBtn: {
      backgroundColor: 'transparent',
      border: '1px solid rgba(255,255,255,0.5)',
      color: '#FFF',
      padding: '6px 12px',
      borderRadius: t.radius.sm,
      cursor: 'pointer',
      fontSize: t.fontSize.base,
      fontWeight: t.fontWeight.bold
    },
    addBtn: {
      position: 'absolute',
      right: '6px',
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: t.primary,
      color: '#FFF',
      border: 'none',
      borderRadius: t.radius.sm,
      width: '28px',
      height: '28px',
      cursor: 'pointer'
    },

    // --- Table ---
    tableContainer: {
      overflowX: 'auto',
      backgroundColor: t.bgMain,
      borderRadius: t.radius.md,
      border: `1px solid ${t.border}`
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      textAlign: 'left',
      minWidth: '800px'
    },
    th: {
      color: t.textMuted,
      padding: '10px 14px',
      backgroundColor: t.bgSubtle,
      fontSize: t.fontSize.sm,
      fontWeight: t.fontWeight.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
      borderBottom: `1px solid ${t.border}`
    },
    td: {
      padding: '10px 14px',
      color: t.textMain,
      fontSize: t.fontSize.md,
      lineHeight: 1.4
    },
    tr: { borderBottom: `1px solid ${t.border}` },
    emptyCell: { padding: '28px 14px', color: t.textMuted, fontSize: t.fontSize.md, textAlign: 'center' },

    // --- Pagination ---
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: t.spacing.md,
      padding: '12px 15px',
      backgroundColor: t.bgMain,
      borderRadius: t.radius.md,
      border: `1px solid ${t.border}`
    },
    pageBtn: {
      backgroundColor: t.bgMain,
      border: `1px solid ${t.borderStrong}`,
      color: t.textMain,
      padding: '6px 12px',
      marginLeft: t.spacing.sm,
      borderRadius: t.radius.md,
      cursor: 'pointer',
      fontWeight: t.fontWeight.bold,
      fontSize: t.fontSize.base
    },
    pageInfo: { color: t.textMuted, fontSize: t.fontSize.base, fontWeight: t.fontWeight.medium },

    // --- Form ---
    formWrapper: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: t.bgMain,
      borderRadius: t.radius.lg,
      border: `1px solid ${t.border}`,
      overflow: 'hidden'
    },
    formHeaderContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: t.bgSubtle,
      borderBottom: `1px solid ${t.border}`,
      borderLeft: `3px solid ${t.primary}`,
      padding: '14px 20px'
    },
    formHeaderTitle: { margin: 0, color: t.textMain, fontSize: t.fontSize.lg, fontWeight: t.fontWeight.bold },
    formBody: { padding: t.spacing.xl },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: t.spacing.lg },
    formFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: t.spacing.lg,
      borderTop: `1px solid ${t.border}`,
      paddingTop: t.spacing.lg,
      gap: t.spacing.sm
    },
    inputGroup: { display: 'flex', flexDirection: 'column' },
    fullWidth: { gridColumn: '1 / -1' },
    label: {
      marginBottom: '6px',
      color: t.textMuted,
      fontSize: t.fontSize.sm,
      fontWeight: t.fontWeight.bold
    },
    input: {
      padding: '9px 12px',
      border: `1px solid ${t.borderStrong}`,
      borderRadius: t.radius.md,
      fontSize: t.fontSize.md,
      backgroundColor: t.bgInput,
      color: t.textMain,
      boxSizing: 'border-box',
      width: '100%'
    },
    searchInput: {
      flexGrow: 1,
      padding: '9px 14px',
      border: `1px solid ${t.borderStrong}`,
      borderRadius: t.radius.md,
      fontSize: t.fontSize.md,
      backgroundColor: t.bgInput,
      color: t.textMain,
      boxSizing: 'border-box'
    },
    // legacy alias used by existing modules
    ajaxSearchInput: {
      flexGrow: 1,
      padding: '9px 14px',
      border: `1px solid ${t.borderStrong}`,
      borderRadius: t.radius.md,
      fontSize: t.fontSize.md,
      backgroundColor: t.bgInput,
      color: t.textMain,
      boxSizing: 'border-box'
    },

    // --- Dropdown ---
    dropdownMenu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: t.bgRaised,
      border: `1px solid ${t.border}`,
      borderTop: 'none',
      borderRadius: `0 0 ${t.radius.sm} ${t.radius.sm}`,
      maxHeight: '250px',
      overflowY: 'auto',
      zIndex: t.zIndex.dropdown,
      display: 'flex',
      flexDirection: 'column'
    },
    dropdownItem: {
      padding: '10px 14px',
      cursor: 'pointer',
      borderBottom: `1px solid ${t.borderDashed}`,
      color: t.textMain,
      fontSize: t.fontSize.md
    },
    dropdownFooter: {
      padding: '10px 14px',
      backgroundColor: isDarkMode ? '#333' : t.primarySoft,
      color: t.primaryText,
      fontWeight: t.fontWeight.bold,
      fontSize: t.fontSize.base,
      textAlign: 'center',
      cursor: 'pointer',
      borderTop: `1px solid ${t.border}`,
      position: 'sticky',
      bottom: 0
    },

    // --- Drawer / modal ---
    drawerOverlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: t.overlay,
      zIndex: t.zIndex.drawer,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: '80px',
      paddingBottom: t.spacing.lg
    },
    drawerPanel: {
      width: '90%',
      maxWidth: '500px',
      maxHeight: '100%',
      backgroundColor: t.bgMain,
      color: t.textMain,
      borderRadius: t.radius.xl,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    drawerHeader: {
      backgroundColor: t.primary,
      padding: '20px 30px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    drawerTitle: { margin: 0, color: '#FFF', fontSize: '24px' },
    drawerBody: { padding: t.spacing.xxl, overflowY: 'auto', flexGrow: 1 },
    drawerFooter: {
      padding: '20px 30px',
      backgroundColor: t.bgSubtle,
      borderTop: `1px solid ${t.border}`
    },
    drawerCloseBtn: {
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      color: '#FFF',
      width: '32px',
      height: '32px',
      borderRadius: t.radius.circle,
      cursor: 'pointer',
      fontSize: t.fontSize.lg
    },
    drawerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: `1px dashed ${t.borderDashed}`
    },
    drawerLabel: { color: t.textMuted, fontSize: t.fontSize.md, fontWeight: t.fontWeight.bold },
    drawerValue: { color: t.textMain, fontSize: t.fontSize.md, fontWeight: t.fontWeight.bold, textAlign: 'right' },

    // --- Notification ---
    notificationOverlay: {
      position: 'fixed',
      top: 0, left: 0,
      width: '100vw', height: '100vh',
      backgroundColor: 'rgba(0,0,0,0.4)',
      zIndex: t.zIndex.notification,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    notificationBox: {
      backgroundColor: t.bgMain,
      padding: '24px 32px',
      borderRadius: t.radius.lg,
      boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
      minWidth: '300px',
      maxWidth: '80%',
      textAlign: 'center'
    },
    notificationTitle: { margin: '0 0 10px 0', color: t.textMain, fontSize: '20px' },
    notificationText: { margin: '0 0 24px 0', color: t.textMuted, fontSize: t.fontSize.lg, lineHeight: '1.5' },
    badge: {
      backgroundColor: t.primarySoft,
      color: t.primaryText,
      padding: '3px 9px',
      borderRadius: t.radius.pill,
      fontSize: t.fontSize.sm,
      fontWeight: t.fontWeight.bold,
      whiteSpace: 'nowrap',
      display: 'inline-block',
      lineHeight: 1.5
    },
    dangerTabBtn: {
      backgroundColor: t.bgMain,
      border: `1px solid ${hexToRgba(t.danger, 0.35)}`,
      color: t.danger,
      padding: '7px 14px',
      marginLeft: 0,
      borderRadius: t.radius.pill,
      cursor: 'pointer',
      fontWeight: t.fontWeight.medium,
      fontSize: t.fontSize.base
    },
    deleteBtn: {
      backgroundColor: 'transparent',
      color: t.danger,
      border: `1px solid ${hexToRgba(t.danger, 0.35)}`,
      padding: '6px 12px',
      borderRadius: t.radius.md,
      cursor: 'pointer',
      fontSize: t.fontSize.base,
      fontWeight: t.fontWeight.bold
    },
    notificationBtn: {
      backgroundColor: t.primary,
      color: '#FFFFFF',
      padding: '10px 24px',
      border: 'none',
      borderRadius: t.radius.sm,
      cursor: 'pointer',
      fontSize: '15px',
      fontWeight: t.fontWeight.bold,
      width: '100%'
    },

    // --- Cards / widgets ---
    card: {
      position: 'relative',
      color: t.textMain,
      borderRadius: t.radius.lg,
      padding: t.spacing.lg,
      backgroundColor: t.bgMain,
      border: `1px solid ${t.border}`,
      boxShadow: t.shadowCard
    },
    widgetBox: { borderRadius: t.radius.xl, overflow: 'hidden' },
    widgetHeader: { padding: '15px 20px' },
    widgetTitle: { margin: 0, fontSize: t.fontSize.lg, fontWeight: t.fontWeight.bold, color: t.textMain }
  };
};

// Pass isDarkMode explicitly (legacy prop-drilled modules) or omit it to read
// from ThemeContext. `accent` sets the module's own colour — see MODULE_ACCENTS
// in utils/constants.js.
export const useStyles = (
  isDarkModeOverride?: boolean,
  accentOverride?: string
): AppStyles => {
  const ctx = useTheme();
  const ctxAccent = useAccent();
  const isDarkMode = isDarkModeOverride === undefined ? ctx.isDarkMode : isDarkModeOverride;
  // Precedence: an explicit override, then the module's own accent, then the
  // parish brand colour. Modules with a deliberate identity colour (Attendance
  // orange, Assets teal) keep it; the rest follow the brand.
  const accent = accentOverride || ctxAccent || ctx.brandColor || undefined;
  return useMemo(() => buildStyles(isDarkMode, accent), [isDarkMode, accent]);
};

export default useStyles;
